import api, { type ExtendedAxiosRequestConfig } from 'api/axiosInstance';
import mime from 'mime/lite';
import type { AxiosRequestConfig } from 'axios';
import qs from 'query-string';
import i18n from 'i18n';
import { ResponseType } from 'types/response.types';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import { globalyAcceptedFileUploaderTypes } from 'utils/constants';
import showToast from 'utils/toast';

type UploadURLResponse = {
  url: { url: string; fields: { key: string; payload: string } };
};

const TIMEOUT_IN_MILLISECONDS = 20000;

let n = 1; // very important,
// it looks like safari can auto cache a response and re use it if a an identical request made again in very short time
// but we never need a cached response in this case
// so we are differentiating between thes requests with a new query param

export const getUploadURL = (
  params: {
    category: string;
    contentType: string;
  },
  config?: ExtendedAxiosRequestConfig,
): Promise<UploadURLResponse> =>
  api.get(`/v2/users/upload-url?${qs.stringify({ ...params, n: n++ })}`, {
    timeout: TIMEOUT_IN_MILLISECONDS,
    ...config,
  });

export const uploadFileToS3 = (
  { url, file, fields }: { url: string; file: Blob; fields: object },
  config?: ExtendedAxiosRequestConfig,
): Promise<any> => {
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  formData.append('file', file);
  return api
    .post(url, formData, {
      ...config,
      noHeaders: true,
      noOnBehalf: true,
    } as ExtendedAxiosRequestConfig)
    .then(() => {
      return fields;
    });
};

type UploadFileOptions = {
  file: File;
  onProgress?: (n: number) => void;
  category?: UploadTypesEnum;
  cancelToken?: AxiosRequestConfig['cancelToken'];
};

const unifyFileType = (fileType: string) => {
  if (fileType === ResponseType.textXml) return ResponseType.applicationXml;
  return fileType;
};

const getMimeTypeFromFile = (file: File): string | null => {
  if (file.type) {
    return unifyFileType(file.type);
  }

  const mimeTypeFromName = mime.getType(file.name);
  if (mimeTypeFromName) {
    return unifyFileType(mimeTypeFromName);
  }

  return null;
};

export const uploadFile = async ({
  file,
  onProgress,
  category,
  cancelToken,
}: UploadFileOptions) => {
  const contentType = getMimeTypeFromFile(file);

  if (!contentType) {
    const allowedExtensions = Object.values(globalyAcceptedFileUploaderTypes)
      .flat()
      .join(', ');
    showToast({
      title: `${i18n.t(
        'file_uploader.not_allowed_extension',
      )} ${allowedExtensions}`,
      variant: 'error',
    });
    throw new Error('Unsupported file type');
  }

  const {
    url: { url, fields },
  } = await getUploadURL(
    {
      category: category || UploadTypesEnum.document,
      contentType,
    },
    { cancelToken },
  );
  return await uploadFileToS3(
    { url, fields, file },
    {
      onUploadProgress: (e) => onProgress?.((e.loaded / e.total) * 100),
      cancelToken,
    },
  ).then(() => fields);
};
